import { useState } from 'react';
import { useAppStore } from '../../store/appStore';
import './Settings.css';

export const Settings = () => {
  const { user } = useAppStore();
  const [language, setLanguage] = useState('en');
  const [voiceGender, setVoiceGender] = useState<'male' | 'female'>('female');
  const [voiceRate, setVoiceRate] = useState(1.0);
  const [voicePitch, setVoicePitch] = useState(1.0);
  const [particleTheme, setParticleTheme] = useState('default');
  const [emotionDetection, setEmotionDetection] = useState(true);
  const [gestureSmoothing, setGestureSmoothing] = useState(true);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.7);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'hi', name: 'Hindi' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ar', name: 'Arabic' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' }
  ];

  const particleThemes = [
    { id: 'default', name: 'Default' },
    { id: 'fire', name: 'Fire' },
    { id: 'water', name: 'Water' },
    { id: 'nature', name: 'Nature' },
    { id: 'neon', name: 'Neon' },
    { id: 'galaxy', name: 'Galaxy' }
  ];

  const saveSettings = () => {
    const settings = {
      language,
      voiceGender,
      voiceRate,
      voicePitch,
      particleTheme,
      emotionDetection,
      gestureSmoothing,
      confidenceThreshold
    };

    localStorage.setItem('aix-sync-settings', JSON.stringify(settings));
    alert('Settings saved successfully!');
  };

  const resetSettings = () => {
    if (confirm('Reset all settings to default?')) {
      setLanguage('en');
      setVoiceGender('female');
      setVoiceRate(1.0);
      setVoicePitch(1.0);
      setParticleTheme('default');
      setEmotionDetection(true);
      setGestureSmoothing(true);
      setConfidenceThreshold(0.7);
      localStorage.removeItem('aix-sync-settings');
    }
  };

  return (
    <div className="settings-panel">
      <h2>Settings</h2>

      <div className="settings-section">
        <h3>Language & Translation</h3>
        <div className="setting-item">
          <label>Interface Language:</label>
          <select value={language} onChange={(e) => setLanguage(e.target.value)}>
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>{lang.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="settings-section">
        <h3>Voice Settings</h3>
        <div className="setting-item">
          <label>Voice Gender:</label>
          <select value={voiceGender} onChange={(e) => setVoiceGender(e.target.value as 'male' | 'female')}>
            <option value="female">Female</option>
            <option value="male">Male</option>
          </select>
        </div>
        
        <div className="setting-item">
          <label>Speech Rate: {voiceRate.toFixed(1)}x</label>
          <input
            type="range"
            min="0.5"
            max="2.0"
            step="0.1"
            value={voiceRate}
            onChange={(e) => setVoiceRate(parseFloat(e.target.value))}
          />
        </div>
        
        <div className="setting-item">
          <label>Speech Pitch: {voicePitch.toFixed(1)}x</label>
          <input
            type="range"
            min="0.5"
            max="2.0"
            step="0.1"
            value={voicePitch}
            onChange={(e) => setVoicePitch(parseFloat(e.target.value))}
          />
        </div>
      </div>

      <div className="settings-section">
        <h3>Visual Settings</h3>
        <div className="setting-item">
          <label>Particle Theme:</label>
          <select value={particleTheme} onChange={(e) => setParticleTheme(e.target.value)}>
            {particleThemes.map(theme => (
              <option key={theme.id} value={theme.id}>{theme.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="settings-section">
        <h3>Recognition Settings</h3>
        <div className="setting-item checkbox-item">
          <label>
            <input
              type="checkbox"
              checked={emotionDetection}
              onChange={(e) => setEmotionDetection(e.target.checked)}
            />
            Enable Emotion Detection
          </label>
        </div>
        
        <div className="setting-item checkbox-item">
          <label>
            <input
              type="checkbox"
              checked={gestureSmoothing}
              onChange={(e) => setGestureSmoothing(e.target.checked)}
            />
            Enable Gesture Smoothing
          </label>
        </div>
        
        <div className="setting-item">
          <label>Confidence Threshold: {(confidenceThreshold * 100).toFixed(0)}%</label>
          <input
            type="range"
            min="0.5"
            max="0.95"
            step="0.05"
            value={confidenceThreshold}
            onChange={(e) => setConfidenceThreshold(parseFloat(e.target.value))}
          />
        </div>
      </div>

      <div className="settings-actions">
        <button onClick={saveSettings} className="btn btn-primary">
          Save Settings
        </button>
        <button onClick={resetSettings} className="btn btn-secondary">
          Reset to Default
        </button>
      </div>

      {user && (
        <div className="user-info">
          <h3>Account</h3>
          <p>Email: {user.email}</p>
          <p>Name: {user.name}</p>
        </div>
      )}
    </div>
  );
};
